/**
 * DemoBanner - Persistent yellow banner shown across the app when the
 * logged-in user is a demo session.
 *
 * Includes:
 *  - "DEMO MODE — Synthetic Data Only" message
 *  - Live countdown timer (auto-logout when 0)
 *  - "Sign up for full access" CTA
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Sparkles, Clock, ArrowRight, Lock } from 'lucide-react';

const DemoBanner = () => {
  const { user, logout } = useAuth();
  const [secondsLeft, setSecondsLeft] = useState(null);

  const isDemo = user?.is_demo || user?.email?.endsWith('@hospiceguard.demo');
  const expiresAt = user?.demo_session_expires_at;

  useEffect(() => {
    if (!isDemo || !expiresAt) return;

    const tick = () => {
      const ms = new Date(expiresAt).getTime() - Date.now();
      setSecondsLeft(Math.max(0, Math.floor(ms / 1000)));
      if (ms <= 0) {
        // Auto-logout on expiry
        try {
          logout?.();
        } catch (e) {
          // best-effort
        }
      }
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isDemo, expiresAt, logout]);

  if (!isDemo) return null;

  const minutes = secondsLeft != null ? Math.floor(secondsLeft / 60) : null;
  const seconds = secondsLeft != null ? secondsLeft % 60 : null;
  const timeStr =
    minutes != null
      ? `${minutes}:${String(seconds).padStart(2, '0')}`
      : '—';

  return (
    <div
      className="bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 text-amber-950 border-b-2 border-amber-600/40 shadow-md sticky top-0 z-50"
      data-testid="demo-mode-banner"
    >
      <div className="max-w-screen-2xl mx-auto px-3 sm:px-4 py-2 flex items-center justify-between gap-2 flex-wrap text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-4 h-4 shrink-0" />
          <span className="font-bold uppercase tracking-wide text-xs sm:text-sm">
            Demo Mode
          </span>
          <span
            className="hidden md:inline-flex items-center gap-1 rounded-full border border-amber-900/40 bg-amber-950/10 text-amber-950 uppercase tracking-wider font-semibold px-2 py-0.5 text-[10px]"
            data-testid="demo-readonly-badge"
            title="All create/update/delete actions are disabled in the demo."
          >
            <Lock className="w-3 h-3" />
            Read-Only
          </span>
          <span className="hidden sm:inline text-amber-900/80 truncate">
            — Synthetic data only. No real PHI. Resets nightly.
          </span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-1 font-mono text-xs sm:text-sm bg-white/40 px-2 py-1 rounded-md">
            <Clock className="w-3 h-3" />
            <span data-testid="demo-countdown">{timeStr}</span>
          </div>
          <Link to="/register">
            <Button
              size="sm"
              className="bg-amber-950 hover:bg-amber-900 text-amber-100 h-8 text-xs"
              data-testid="demo-signup-cta"
            >
              Sign Up Free <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DemoBanner;
